/**
 * NIP-89 application handlers — "open this in something that understands it".
 *
 * The other half of the answer to an open kind set. NIP-31 `alt` lets an unknown
 * event *say* what it is; this lets the reader do something about it. Together they
 * are why Setu can decline to implement a dozen vertical apps without those apps'
 * events becoming dead rows: a kind Setu will never render is still readable as a
 * line of text and still openable in the thing that was built for it.
 *
 * Two kinds are involved and neither is useful alone:
 *
 *  - **31990, handler information.** The app describes itself and lists the kinds it
 *    handles, with URL templates per NIP-19 entity type.
 *  - **31989, handler recommendation.** Someone says "this app handles kind N",
 *    pointing at a 31990 address. This is the discovery path, and it is
 *    deliberately social: recommendations from accounts the reader follows, not a
 *    global registry. There is no authority here to ask.
 *
 * ## The security boundary, which is the interesting part
 *
 * A handler URL template is a **stranger's string that becomes a link the reader
 * clicks**. That makes this module's validation different from the upload path's,
 * and the difference is worth being explicit about: the upload validator exists to
 * stop *us* fetching something (an SSRF concern, hence private-host blocking),
 * while this one exists to stop the reader *navigating* somewhere dangerous. So the
 * rules here are `https` only — which excludes `javascript:`, `data:` and every
 * other scheme that turns a link into code — no embedded credentials, and no
 * template that fails to parse once the entity is substituted.
 *
 * What this module does not do is decide whether the destination is *trustworthy*.
 * It cannot: a handler is an arbitrary third-party web app. The caller's job is to
 * show the reader where they are about to go, which is why `handlerUrl` returns a
 * URL rather than performing a navigation.
 */

import { isHex32 } from "./hex";
import { Kind } from "./kinds";
import { getTagged, type HasTags } from "./tags";
import type { Hex32 } from "./types";

/** NIP-19 entity types a handler template can be parameterised by. */
export type HandlerEntity = "nevent" | "naddr" | "nprofile" | "npub" | "note";

/** The placeholder a handler template puts the encoded entity in. */
const PLACEHOLDER = "<bech32>";

/** How many handler URL templates one event may declare. */
const MAX_TEMPLATES = 16;

/** How many kinds one handler may claim. Generous; a bound, not a policy. */
const MAX_KINDS = 64;

/** One `web` template from a handler, and which entity type it expects. */
export interface HandlerWebTemplate {
  /**
   * The entity type this template takes, when the handler said. Undefined means the
   * handler declared a general template — NIP-89 allows the marker to be omitted,
   * and such a template is the fallback for any entity type.
   */
  readonly entity?: HandlerEntity;
  /** The raw template, containing `<bech32>`. Unvalidated until substituted. */
  readonly template: string;
}

/** A parsed kind-31990. */
export interface AppHandler {
  /** `31990:<pubkey>:<d>` — the addressable coordinate a recommendation names. */
  readonly address: string;
  readonly pubkey: Hex32;
  /** The `d` tag. May be empty, which is a valid addressable identifier. */
  readonly identifier: string;
  /** Kinds the handler claims to open. Empty means it claimed none. */
  readonly kinds: readonly number[];
  /** Display name from the handler's own metadata, if it parsed. */
  readonly name?: string;
  readonly about?: string;
  /** Icon URL, as published. The caller's image policy decides whether to load it. */
  readonly pictureUrl?: string;
  /** `web` templates only. Native-platform templates are useless to a web client. */
  readonly webTemplates: readonly HandlerWebTemplate[];
}

const ENTITIES = new Set<string>([
  "nevent",
  "naddr",
  "nprofile",
  "npub",
  "note",
]);

/** The entity marker on a handler tag, when it is one we recognise. */
function entityOf(marker: string | undefined): HandlerEntity | undefined {
  if (marker === undefined) return undefined;
  const lower = marker.trim().toLowerCase();
  return ENTITIES.has(lower) ? (lower as HandlerEntity) : undefined;
}

/**
 * The handler's self-description, from its kind-0-shaped content.
 *
 * Failure is silent and total: a handler whose content is not an object is still a
 * usable handler, it just has no name. Throwing here would let one malformed
 * handler event remove every *other* handler from the reader's menu, because the
 * caller parses a list.
 */
function metadataOf(content: string): {
  name?: string;
  about?: string;
  picture?: string;
} {
  if (content.trim() === "") return {};
  try {
    const parsed: unknown = JSON.parse(content);
    if (typeof parsed !== "object" || parsed === null) return {};
    const record = parsed as Record<string, unknown>;
    const pick = (key: string): string | undefined => {
      const value = record[key];
      return typeof value === "string" && value.trim() !== ""
        ? value.trim()
        : undefined;
    };
    return {
      ...((pick("display_name") ?? pick("name"))
        ? { name: pick("display_name") ?? pick("name") }
        : {}),
      ...(pick("about") ? { about: pick("about") } : {}),
      ...(pick("picture") ? { picture: pick("picture") } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * Parse a kind-31990, or undefined when it cannot name itself.
 *
 * A handler with no `web` template is dropped rather than returned empty: on the
 * web it can never be opened, so offering it would be a menu entry that does
 * nothing. A handler claiming no kinds is kept — the caller matches on kind, so it
 * simply never matches, and that is different from being malformed.
 */
export function parseAppHandler(
  event: HasTags & {
    readonly kind: number;
    readonly pubkey: string;
    readonly content: string;
  },
): AppHandler | undefined {
  if (event.kind !== Kind.AppHandler) return undefined;
  if (!isHex32(event.pubkey)) return undefined;

  const identifier = getTagged(event, "d")[0]?.[1] ?? "";

  const kinds: number[] = [];
  for (const tag of getTagged(event, "k")) {
    if (kinds.length >= MAX_KINDS) break;
    const raw = tag[1];
    if (raw === undefined) continue;
    const parsed = Number(raw);
    // `Number("")` is 0 and `Number("1e3")` is 1000; require a plain integer so a
    // handler cannot claim a kind by writing it in a form no relay would index.
    if (!/^\d+$/.test(raw.trim()) || !Number.isSafeInteger(parsed)) continue;
    if (!kinds.includes(parsed)) kinds.push(parsed);
  }

  const webTemplates: HandlerWebTemplate[] = [];
  for (const tag of getTagged(event, "web")) {
    if (webTemplates.length >= MAX_TEMPLATES) break;
    const template = tag[1]?.trim();
    if (!template?.includes(PLACEHOLDER)) continue;
    const entity = entityOf(tag[2]);
    webTemplates.push({ template, ...(entity ? { entity } : {}) });
  }
  if (webTemplates.length === 0) return undefined;

  const meta = metadataOf(event.content);
  return {
    address: `${Kind.AppHandler}:${event.pubkey}:${identifier}`,
    pubkey: event.pubkey,
    identifier,
    kinds,
    webTemplates,
    ...(meta.name !== undefined ? { name: meta.name } : {}),
    ...(meta.about !== undefined ? { about: meta.about } : {}),
    ...(meta.picture !== undefined ? { pictureUrl: meta.picture } : {}),
  };
}

/**
 * The kind a recommendation is about, from its `d` tag.
 *
 * Undefined for anything that is not a plain kind number, which is the same
 * strictness `parseAppHandler` applies to `k` — a recommendation whose subject
 * cannot be read is not a recommendation for kind 0.
 */
export function recommendationKind(
  event: HasTags & { readonly kind: number },
): number | undefined {
  if (event.kind !== Kind.AppHandlerRecommendation) return undefined;
  const raw = getTagged(event, "d")[0]?.[1]?.trim();
  if (raw === undefined || !/^\d+$/.test(raw)) return undefined;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

/** A handler address a recommendation points at, with its relay hint. */
export interface RecommendedHandler {
  /** `31990:<pubkey>:<d>`. */
  readonly address: string;
  readonly relayHint?: string;
}

/**
 * Handler addresses a kind-31989 recommends, deduplicated in tag order.
 *
 * Only `a` tags naming a 31990 are returned. A recommendation pointing at some
 * other kind is either malformed or aimed at something that is not a handler, and
 * following it would put an arbitrary address into a filter.
 */
export function recommendedHandlers(
  event: HasTags & { readonly kind: number },
): readonly RecommendedHandler[] {
  if (event.kind !== Kind.AppHandlerRecommendation) return [];
  const out: RecommendedHandler[] = [];
  const seen = new Set<string>();
  const prefix = `${Kind.AppHandler}:`;
  for (const tag of getTagged(event, "a")) {
    const address = tag[1]?.trim();
    if (!address?.startsWith(prefix) || seen.has(address)) continue;
    // `31990:<64 hex>:<d>` — the pubkey must be real, or the address cannot be
    // queried and would only ever widen a filter for nothing.
    const parts = address.split(":");
    if (parts.length < 3 || !isHex32(parts[1] ?? "")) continue;
    seen.add(address);
    const relayHint = tag[2]?.trim();
    out.push({ address, ...(relayHint ? { relayHint } : {}) });
  }
  return out;
}

/** Which NIP-19 prefix an encoded entity uses, for template matching. */
function entityOfBech32(bech32: string): HandlerEntity | undefined {
  for (const entity of ENTITIES) {
    if (bech32.startsWith(`${entity}1`)) return entity as HandlerEntity;
  }
  return undefined;
}

/**
 * The URL that opens `bech32` in this handler, or undefined when none is safe.
 *
 * Prefers a template declared for this entity type and falls back to an unmarked
 * one, because a handler that declares `["web", "...", "nevent"]` and nothing else
 * cannot open an `naddr` — substituting anyway produces a URL the handler will not
 * understand, and a link that lands on an error page is worse than an absent one.
 *
 * The encoded entity is substituted without escaping, which is safe for exactly one
 * reason: bech32 is `[a-z0-9]` only, so it cannot introduce a delimiter, a scheme,
 * a path traversal or a query separator. That property is asserted here rather than
 * assumed — a caller passing something that is not bech32 gets undefined instead of
 * an injected URL.
 */
export function handlerUrl(
  handler: AppHandler,
  bech32: string,
): string | undefined {
  if (!/^[a-z0-9]+$/.test(bech32)) return undefined;
  const entity = entityOfBech32(bech32);

  const exact =
    entity === undefined
      ? undefined
      : handler.webTemplates.find((row) => row.entity === entity);
  const general = handler.webTemplates.find((row) => row.entity === undefined);
  const chosen = exact ?? general;
  if (chosen === undefined) return undefined;

  const candidate = chosen.template.split(PLACEHOLDER).join(bech32);
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return undefined;
  }
  // `https` only. This is what keeps `javascript:` and `data:` templates — which
  // would execute in the reader's tab rather than navigate — out of an href.
  if (url.protocol !== "https:") return undefined;
  if (url.username !== "" || url.password !== "") return undefined;
  return url.toString();
}
